/**
 * Shared CSV / file download helpers for export endpoints.
 */

const withExtension = (name, ext) => {
  const base = String(name || "")
    .replace(/\.[^.]+$/, "")
    .trim();
  return `${base || "export"}.${ext}`;
};

const looksLikeCsv = (text) => {
  const firstLine =
    String(text || "")
      .split(/\r?\n/)
      .find((line) => line.trim()) || "";
  return firstLine.includes(",");
};

/**
 * GET a CSV (or spreadsheet) export and trigger a browser download.
 * @param {{ path: string, accessToken: string, fileName?: string, query?: Record<string, string|number|undefined|null> }} opts
 */
export async function downloadCsvExport({
  path,
  accessToken,
  fileName,
  query,
}) {
  const headers = {
    "ngrok-skip-browser-warning": "69420",
    Accept:
      "text/csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*",
    Authorization: `${accessToken}`,
  };

  const base = process.env.REACT_APP_BACKEND_URL || "";
  const cleanPath = String(path || "").replace(/^\//, "");
  const params = new URLSearchParams();
  if (query && typeof query === "object") {
    Object.entries(query).forEach(([key, value]) => {
      if (value != null && value !== "") params.set(key, String(value));
    });
  }
  const qs = params.toString();
  const url = `${base}${cleanPath}${qs ? `?${qs}` : ""}`;

  const response = await fetch(url, { method: "GET", headers });
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    let message = text || `Failed to download export (${response.status})`;
    try {
      const json = JSON.parse(text);
      message = json?.error || json?.message || message;
    } catch {
      /* plain text */
    }
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }

  const contentType = (response.headers.get("content-type") || "").toLowerCase();
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  const asText = new TextDecoder().decode(bytes);

  let downloadName = fileName || "export";
  const disposition = response.headers.get("content-disposition") || "";
  const match = disposition.match(
    /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i
  );
  if (match) {
    downloadName = decodeURIComponent(match[1] || match[2]).trim();
  }

  let blob;
  if (
    contentType.includes("spreadsheetml") ||
    contentType.includes("application/vnd.ms-excel") ||
    /\.xlsx$/i.test(downloadName)
  ) {
    blob = new Blob([bytes], {
      type:
        contentType ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    if (!/\.xlsx?$/i.test(downloadName)) {
      downloadName = withExtension(downloadName, "xlsx");
    }
  } else if (
    contentType.includes("text/csv") ||
    contentType.includes("application/csv") ||
    looksLikeCsv(asText) ||
    /\.csv$/i.test(downloadName)
  ) {
    const csvWithBom =
      asText.charCodeAt(0) === 0xfeff ? asText : `\uFEFF${asText}`;
    blob = new Blob([csvWithBom], { type: "text/csv;charset=utf-8" });
    downloadName = withExtension(downloadName, "csv");
  } else if (
    contentType.includes("application/json") ||
    asText.trim().startsWith("{")
  ) {
    let json;
    try {
      json = JSON.parse(asText);
    } catch {
      throw new Error("Server did not return a valid export file");
    }
    throw new Error(
      json?.error || json?.message || "Server did not return a CSV export"
    );
  } else {
    // Fallback: save raw bytes with .csv if name has no extension
    blob = new Blob([bytes], { type: contentType || "application/octet-stream" });
    if (!/\.[a-z0-9]+$/i.test(downloadName)) {
      downloadName = withExtension(downloadName, "csv");
    }
  }

  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = downloadName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
}

/** GET /users/export — Companies / Employees */
export const downloadUsersCsv = ({ accessToken, roles, fileName }) =>
  downloadCsvExport({
    path: "users/export",
    accessToken,
    fileName: fileName || (roles ? `${roles}-export` : "users-export"),
    query: roles ? { roles } : undefined,
  });

/** GET /users/:userId/export — one user */
export const downloadUserCsv = ({ accessToken, userId, fileName }) =>
  downloadCsvExport({
    path: `users/${userId}/export`,
    accessToken,
    fileName: fileName || `user-${userId}`,
  });

/** GET /applications/user/:id/export — one user's applications */
export const downloadUserApplicationsCsv = ({
  accessToken,
  userId,
  fileName,
}) =>
  downloadCsvExport({
    path: `applications/user/${userId}/export`,
    accessToken,
    fileName: fileName || `user-${userId}-applications`,
  });

/** GET /applications/export — applications (optional company_id) */
export const downloadApplicationsCsv = ({
  accessToken,
  companyId,
  fileName,
  query,
}) =>
  downloadCsvExport({
    path: "applications/export",
    accessToken,
    fileName:
      fileName ||
      (companyId ? `company-${companyId}-applications` : "applications-export"),
    query: {
      ...(query || {}),
      ...(companyId != null && companyId !== ""
        ? { company_id: companyId }
        : {}),
    },
  });
